import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import { Clapperboard, Loader2, Unlink } from 'lucide-react';

interface GuildProject {
  id: string;
  title: string;
  work_id: string | null;
}

interface GuildProjectLinkCardProps {
  workId: string;
}

export default function GuildProjectLinkCard({ workId }: GuildProjectLinkCardProps) {
  const { publisher } = useAuth();
  const [projects, setProjects] = useState<GuildProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const linkedProject = projects.find(p => p.work_id === workId) ?? null;

  useEffect(() => {
    if (!publisher?.id) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const { data, error } = await supabase
        .from('guild_projects')
        .select('id, title, work_id')
        .eq('publisher_id', publisher.id)
        .order('title');
      if (cancelled) return;
      if (error) {
        toast.error('Failed to load guild projects');
      } else {
        setProjects((data as GuildProject[]) ?? []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [publisher?.id, workId]);

  const handleLink = async (projectId: string) => {
    if (!publisher?.id || projectId === 'none') return;
    setSaving(true);
    try {
      const { error: clearError } = await supabase
        .from('guild_projects')
        .update({ work_id: null })
        .eq('publisher_id', publisher.id)
        .eq('work_id', workId);
      if (clearError) throw clearError;

      const { error } = await supabase
        .from('guild_projects')
        .update({ work_id: workId })
        .eq('id', projectId)
        .eq('publisher_id', publisher.id);
      if (error) throw error;

      setProjects(prev =>
        prev.map(p => ({
          ...p,
          work_id: p.id === projectId ? workId : p.work_id === workId ? null : p.work_id,
        })),
      );
      toast.success('Guild project linked — credits sync on publish');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to link guild project');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async () => {
    if (!publisher?.id || !linkedProject) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('guild_projects')
        .update({ work_id: null })
        .eq('id', linkedProject.id)
        .eq('publisher_id', publisher.id);
      if (error) throw error;

      setProjects(prev =>
        prev.map(p => (p.id === linkedProject.id ? { ...p, work_id: null } : p)),
      );
      toast.success('Guild project unlinked');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Failed to unlink guild project');
    } finally {
      setSaving(false);
    }
  };

  if (!publisher?.id) return null;

  return (
    <Card className="partner-card">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Clapperboard className="h-4 w-4 text-primary" />
          <CardTitle className="text-base">Guild project</CardTitle>
        </div>
        <CardDescription>
          Link this work to a Kinora Guild project for automatic crew credits and revenue splits on publish.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading projects…
          </p>
        ) : projects.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No guild projects linked to this studio yet. Create one in the Kinora Guild app first.
          </p>
        ) : (
          <>
            <Select
              value={linkedProject?.id ?? 'none'}
              onValueChange={(value) => {
                if (value === 'none') {
                  if (linkedProject) void handleUnlink();
                  return;
                }
                void handleLink(value);
              }}
              disabled={saving}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a guild project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No linked project</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.title}
                    {p.work_id && p.work_id !== workId ? ' (linked elsewhere)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {linkedProject && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleUnlink}
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Unlink className="h-4 w-4 mr-2" />
                )}
                Unlink project
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
